import { action, computed, makeObservable, observable } from 'mobx';
import type { RootStore } from '.';
import type { CardIssue } from '@creative-kit/shared';

export type IssueDialogMode = 'report' | 'view';

/**
 * Store for managing the card issue dialog state
 * Handles both reporting new issues and viewing/resolving existing issues
 */
export class UiCardIssueStore {
  private rootStore: RootStore;

  // Dialog visibility
  isDialogOpen = false;
  targetCardIds: string[] = [];
  mode: IssueDialogMode = 'report';

  // Form state
  message = '';

  // Current issue being viewed (for view mode)
  currentIssue: CardIssue | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isDialogOpen: observable,
      targetCardIds: observable,
      mode: observable,
      message: observable,
      currentIssue: observable,
      isBatchMode: computed,
      targetCardCount: computed,
      canReport: computed,
      openReportDialog: action.bound,
      openViewDialog: action.bound,
      setMessage: action.bound,
      closeDialog: action.bound,
      reportIssue: action.bound,
      resolveIssue: action.bound,
    });
  }

  get isBatchMode() {
    return this.targetCardIds.length > 1;
  }

  get targetCardCount() {
    return this.targetCardIds.length;
  }

  get canReport() {
    return this.message.trim().length > 0;
  }

  /**
   * Open the dialog to report a new issue for one or more cards
   */
  openReportDialog(cardIds: string | string[]) {
    const ids = Array.isArray(cardIds) ? cardIds : [cardIds];
    this.targetCardIds = ids;
    this.mode = 'report';
    this.message = '';
    this.currentIssue = null;
    this.isDialogOpen = true;
  }

  /**
   * Open the dialog to view an existing issue (single card only)
   */
  openViewDialog(cardId: string) {
    const card = this.rootStore.cardsStore.cards[cardId];
    if (!card?.data.issue) return;

    this.targetCardIds = [cardId];
    this.mode = 'view';
    this.currentIssue = card.data.issue;
    this.message = card.data.issue.message;
    this.isDialogOpen = true;
  }

  setMessage(text: string) {
    this.message = text;
  }

  closeDialog() {
    this.isDialogOpen = false;
    this.targetCardIds = [];
    this.message = '';
    this.currentIssue = null;
  }

  /**
   * Report an issue for the target cards
   */
  async reportIssue() {
    if (!this.canReport) return;

    const { sessionCardActions, uiSnackbarStore } = this.rootStore;

    try {
      await sessionCardActions.reportCardIssue(this.targetCardIds, this.message.trim());

      const count = this.targetCardIds.length;
      uiSnackbarStore.setSnackbarDetails({
        description: count === 1 ? 'Issue reported' : `Issue reported for ${count} cards`,
      });

      this.closeDialog();
    } catch (error) {
      console.error('Failed to report issue:', error);
      uiSnackbarStore.setSnackbarDetails({
        description: 'Unable to report issue. Please try again or refresh the page.',
      });
    }
  }

  /**
   * Resolve issues for the specified cards
   */
  async resolveIssue(cardIds?: string | string[]) {
    const ids = cardIds ? (Array.isArray(cardIds) ? cardIds : [cardIds]) : this.targetCardIds;

    if (ids.length === 0) return;

    const { sessionCardActions, uiSnackbarStore } = this.rootStore;

    try {
      await sessionCardActions.resolveCardIssue(ids);

      const count = ids.length;
      uiSnackbarStore.setSnackbarDetails({
        description: count === 1 ? 'Issue resolved' : `Resolved issues for ${count} cards`,
      });

      // Close dialog if it's open
      if (this.isDialogOpen) {
        this.closeDialog();
      }
    } catch (error) {
      console.error('Failed to resolve issue:', error);
      uiSnackbarStore.setSnackbarDetails({
        description: 'Unable to resolve issue. Please try again or refresh the page.',
      });
    }
  }
}
